import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderOpen, Plus, SquarePen, Trash2 } from 'lucide-react';
import { collection, deleteDoc, doc, onSnapshot, query, where } from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import LoggedInAppLayout from '../components/LoggedInAppLayout';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase';
import { storageObjectPathFromDownloadUrl } from '../lib/firebaseStoragePaths';
import { getProjectEditRoute, getProjectToolLabel } from '../lib/projectToolType';
import { formatStatusLabel } from '../lib/projectWorkspaceUtils';
import Seo from '../seo/Seo';
import { isAdminEmail } from '../lib/userLimits';

function projectSortKey(p) {
  if (p.createdAt?.toMillis) return p.createdAt.toMillis();
  return 0;
}

function projectCardMeta(project) {
  return [project.roomType, project.style].filter(Boolean).join(' · ');
}

function isStorageNotFound(err) {
  return err?.code === 'storage/object-not-found';
}

function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [listError, setListError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [projectPendingDelete, setProjectPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const deleteDialogTitleId = useId();
  const deleteDialogDescId = useId();

  const performDeleteProject = useCallback(
    async (p) => {
      if (!user?.uid) return;
      setDeleteError('');
      setDeleting(true);
      try {
        await deleteDoc(doc(db, 'projects', p.id));

        const origRef = ref(storage, `users/${user.uid}/projects/${p.id}/original`);
        try {
          await deleteObject(origRef);
        } catch (err) {
          if (!isStorageNotFound(err)) throw err;
        }

        if (p.generatedImageUrl) {
          const genPath = storageObjectPathFromDownloadUrl(p.generatedImageUrl);
          if (genPath) {
            try {
              await deleteObject(ref(storage, genPath));
            } catch (err) {
              if (!isStorageNotFound(err)) throw err;
            }
          }
        }
        setProjectPendingDelete(null);
      } catch (err) {
        setDeleteError(err?.message || 'Could not delete project.');
      } finally {
        setDeleting(false);
      }
    },
    [user?.uid]
  );

  useEffect(() => {
    if (!projectPendingDelete) return undefined;
    function onKeyDown(e) {
      if (e.key === 'Escape') setProjectPendingDelete(null);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [projectPendingDelete]);

  useEffect(() => {
    if (!user?.uid) return undefined;

    const q = query(collection(db, 'projects'), where('userId', '==', user.uid));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setListError('');
        const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => projectSortKey(b) - projectSortKey(a));
        setProjects(rows);
      },
      (err) => {
        setListError(err?.message || 'Could not load projects.');
        setProjects([]);
      }
    );

    return unsub;
  }, [user?.uid]);

  return (
    <>
      <Seo
        title="Projects | MyRoomsAI"
        description="Manage your AI room design projects and track generated interiors in your MyRoomsAI projects page."
        canonicalPath="/projects"
        noindex
      />
      {projectPendingDelete ? (
        <div
          className="delete-project-confirm-backdrop"
          role="presentation"
          onClick={() => !deleting && setProjectPendingDelete(null)}
        >
          <div
            className="card delete-project-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={deleteDialogTitleId}
            aria-describedby={deleteDialogDescId}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id={deleteDialogTitleId} className="delete-project-confirm-title">
              Delete this project?
            </h2>
            <p id={deleteDialogDescId} className="delete-project-confirm-desc">
              <strong>
                {[projectPendingDelete.roomType, projectPendingDelete.style]
                  .filter(Boolean)
                  .join(' · ') || 'This project'}
              </strong>{' '}
              will be removed permanently. This cannot be undone.
            </p>
            <div className="delete-project-confirm-actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={deleting}
                onClick={() => setProjectPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary delete-project-confirm-delete"
                disabled={deleting}
                onClick={() => performDeleteProject(projectPendingDelete)}
              >
                {deleting ? 'Deleting…' : 'Delete project'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <LoggedInAppLayout pageClassName="projects-page">
        <div className="projects-page-container">
          <header className="tool-hub-header projects-page-header">
            <h1 className="tool-hub-heading">
              All{' '}
              <span className="new-design-page-heading-accent">Projects</span>
            </h1>
            <p className="tool-hub-subtitle projects-page-count">
              {projects.length === 1
                ? `You have 1 saved project${isAdminEmail(user?.email) ? '' : ' (max 10)'}`
                : `You have ${projects.length} saved projects${isAdminEmail(user?.email) ? '' : ' (max 10)'}`}
            </p>
          </header>

          <div className="projects-panel card">
            {listError ? (
              <p className="dashboard-inline-error projects-panel-error" role="alert">
                {listError}
              </p>
            ) : null}

            {deleteError ? (
              <p className="dashboard-inline-error projects-panel-error" role="alert">
                {deleteError}
              </p>
            ) : null}

            <section className="dashboard-projects-section projects-panel-content">
              <ul className="dashboard-projects-grid">
                <li>
                  <Link
                    to="/ai-tools/ai-room-decoration"
                    className="card dashboard-project-card dashboard-project-new-card"
                    aria-label="Open design tools"
                  >
                    <div className="dashboard-project-new-card-media">
                      <span className="dashboard-project-new-icon" aria-hidden="true">
                        <Plus size={28} strokeWidth={2} />
                      </span>
                    </div>
                    <div className="dashboard-project-new-card-body">
                      <p className="dashboard-project-new-title">Design Tools</p>
                      <p className="dashboard-project-new-sub">Start a new design</p>
                    </div>
                  </Link>
                </li>
                {projects.map((p) => (
                  <li key={p.id}>
                    <div className="card dashboard-project-card">
                      <Link to={`/projects/${p.id}`} className="dashboard-project-card-link">
                        <div className="dashboard-project-thumb-wrap">
                          {p.originalImageUrl || p.generatedImageUrl ? (
                            <>
                              <img
                                src={p.generatedImageUrl || p.originalImageUrl}
                                alt={`${p.roomType || 'Room'} ${p.style || 'design'} preview`}
                                className="dashboard-project-thumb"
                                loading="lazy"
                                decoding="async"
                                width="360"
                                height="220"
                              />
                              {p.generatedImageUrl && p.originalImageUrl ? (
                                <div
                                  className="dashboard-project-thumb-generated-frame"
                                  aria-hidden="true"
                                >
                                  <img
                                    src={p.originalImageUrl}
                                    alt=""
                                    className="dashboard-project-thumb-generated"
                                    loading="lazy"
                                    decoding="async"
                                    width="180"
                                    height="180"
                                  />
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <div className="dashboard-project-thumb-fallback" aria-hidden />
                          )}
                        </div>
                      </Link>
                      <div className="dashboard-project-card-body dashboard-project-card-body-row">
                        <Link to={`/projects/${p.id}`} className="dashboard-project-card-body-text">
                          <span className="dashboard-project-status">
                            {formatStatusLabel(p.status) || 'Draft'}
                          </span>
                          <p className="dashboard-project-tool">{getProjectToolLabel(p)}</p>
                          {projectCardMeta(p) ? (
                            <p className="dashboard-project-line">{projectCardMeta(p)}</p>
                          ) : null}
                        </Link>
                        <div className="dashboard-project-card-actions">
                          <Link
                            to={`/projects/${p.id}`}
                            className="dashboard-project-open"
                            aria-label="Open project"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FolderOpen size={18.75} strokeWidth={2} aria-hidden="true" />
                          </Link>
                          {getProjectEditRoute(p) ? (
                            <Link
                              to={getProjectEditRoute(p)}
                              className="dashboard-project-edit"
                              aria-label="Edit project"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SquarePen size={18.75} strokeWidth={2} aria-hidden="true" />
                            </Link>
                          ) : null}
                          <button
                            type="button"
                            className="dashboard-project-delete"
                            aria-label="Delete project"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setProjectPendingDelete(p);
                            }}
                          >
                            <Trash2 size={18.75} strokeWidth={2} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </LoggedInAppLayout>
    </>
  );
}

export default ProjectsPage;
